import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDownIcon, 
  QuestionMarkCircleIcon,
  DocumentTextIcon,
  CreditCardIcon,
  CogIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const HelpCenter: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('general');
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'general', name: 'General Questions', icon: QuestionMarkCircleIcon },
    { id: 'processing', name: 'PDF Processing', icon: DocumentTextIcon },
    { id: 'privacy', name: 'Privacy & Security', icon: CreditCardIcon },
    { id: 'technical', name: 'Technical Issues', icon: CogIcon },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: ExclamationTriangleIcon }
  ];

  const faqs: FAQItem[] = useMemo(() => [
    {
      id: 'what-is-notescrafter',
      question: 'What is NotesCrafter?',
      answer: 'NotesCrafter is a free, browser-based PDF tool that enhances and merges PDFs. Transform dark lecture slides to print-ready documents, merge multiple PDFs, remove backgrounds, and optimize for printing - all locally in your browser.',
      category: 'general'
    },
    {
      id: 'is-it-really-free',
      question: 'Is NotesCrafter really free?',
      answer: 'Our Android app is completely free forever with ads. For web access (browser-based), we offer a Community Pass starting at just ₹1 to support development and hosting costs. Try it for ₹1 and decide if you want to continue!',
      category: 'general'
    },
    {
      id: 'android-vs-web',
      question: 'What\'s the difference between Android app and web app?',
      answer: 'Android App: Download from Google Play Store - completely free forever with non-intrusive ads. Process PDFs on your phone anytime. Web App: Access from any browser (desktop/mobile) - requires Community Pass (starts at ₹1). Google Sign in required for authentication. Both versions have the same powerful features!',
      category: 'general'
    },
    {
      id: 'community-pass',
      question: 'What is the Community Pass?',
      answer: 'Community Pass is our affordable web access plan starting at just ₹1. It allows you to use NotesCrafter from any browser with Google Sign in. You can try for ₹1 and upgrade to monthly/yearly plans if you love it. Android app users don\'t need this - the mobile app is always free with ads.',
      category: 'general'
    },
    {
      id: 'how-to-print-online-lecture-notes',
      question: 'How does it work?',
      answer: 'Select your PDF file, choose enhancement options (invert colors, remove background, grayscale, merge PDFs), and click process. Everything happens in your browser using JavaScript - no servers involved.',
      category: 'general'
    },
    {
      id: 'no-account-needed',
      question: 'Do I need to create an account?',
      answer: 'Android App: No account needed - download and use instantly. Web App: Requires Google Sign in for authentication and access validation. This helps us manage Community Pass subscriptions while keeping your data private.',
      category: 'general'
    },
    {
      id: 'file-upload',
      question: 'Are my files uploaded to your servers?',
      answer: 'No. Your files never leave your device. All processing happens locally in your browser using JavaScript. We cannot see, access, or store your PDFs.',
      category: 'privacy'
    },
    {
      id: 'data-collection',
      question: 'What data do you collect?',
      answer: 'We collect anonymous usage statistics (like which features are used) to improve the service. We do NOT collect your files, personal information, or track you across the web. No cookies, no tracking pixels.',
      category: 'privacy'
    },
    {
      id: 'privacy-verify',
      question: 'How can I verify my files aren\'t uploaded?',
      answer: 'Open your browser\'s Developer Tools (F12), go to the Network tab, and process a PDF. You\'ll see NO file uploads. Or disconnect from the internet after the page loads - the tool still works!',
      category: 'privacy'
    },
    {
      id: 'supported-formats',
      question: 'What file formats are supported?',
      answer: 'PDF files only. Most modern PDFs work great, though very large files (100MB+) may be slow depending on your device.',
      category: 'processing'
    },
    {
      id: 'processing-time',
      question: 'How long does processing take?',
      answer: 'Most PDFs process in seconds. Time depends on file size, page count, and your device\'s processing power. Since it runs in your browser, faster computers = faster processing.',
      category: 'processing'
    },
    {
      id: 'merge-pdfs',
      question: 'Can I merge multiple PDFs?',
      answer: 'Yes! Select multiple PDF files and they\'ll be combined into one document. All processing happens locally in your browser.',
      category: 'processing'
    },
    {
      id: 'jpeg2000-issue',
      question: 'What does the JPEG2000 warning mean?',
      answer: 'Some PDFs contain JPEG2000 images that browsers can\'t render. We detect this automatically and offer alternative processing methods (like color inversion using blend modes). You can also remove those pages.',
      category: 'technical'
    },
    {
      id: 'browser-compatibility',
      question: 'Which browsers work best?',
      answer: 'Any modern browser works: Chrome, Firefox, Safari, Edge. Keep your browser updated for best performance. Mobile browsers work too, though desktop is faster for large PDFs.',
      category: 'technical'
    },
    {
      id: 'offline-use',
      question: 'Can I use NotesCrafter offline?',
      answer: 'Yes! Once the page loads, you can disconnect from the internet and still process PDFs. All processing happens locally on your device.',
      category: 'technical'
    },
    {
      id: 'poor-quality',
      question: 'The output quality isn\'t good. How to improve it?',
      answer: 'Start with high-quality PDFs (avoid low-resolution scans). Try different enhancement combinations. For very dark slides, use "Invert Colors". For busy backgrounds, try "Remove Background".',
      category: 'troubleshooting'
    },
    {
      id: 'processing-slow',
      question: 'Processing is slow. What can I do?',
      answer: 'Large PDFs or complex pages take longer. Try: closing other browser tabs, using a faster device, or processing fewer pages at once. Browser-based processing depends on your device\'s power.',
      category: 'troubleshooting'
    },
    {
      id: 'browser-crash',
      question: 'The browser crashes or freezes. Help!',
      answer: 'Very large PDFs (100MB+) or hundreds of pages can overwhelm browsers. Try: splitting the PDF into smaller files, using a desktop browser instead of mobile, or closing other tabs to free up memory.',
      category: 'troubleshooting'
    }
  ], []);

  const filteredFAQs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length === 0) {
      return faqs.filter(faq => faq.category === activeCategory);
    }

    // When searching, search across questions and answers (all categories)
    // We'll return matches (unsorted) here; scoring will be applied later
    return faqs.filter((faq) => {
      const inQuestion = faq.question.toLowerCase().includes(q);
      const inAnswer = faq.answer.toLowerCase().includes(q);
      const inId = faq.id.toLowerCase().includes(q);
      const inCategory = faq.category.toLowerCase().includes(q);
      return inQuestion || inAnswer || inId || inCategory;
    });
  }, [faqs, activeCategory, searchQuery]);

  // Lightweight fuzzy scoring: substring match gets strong score; subsequence match gets medium score
  const fuzzyScore = (text: string, query: string): number => {
    const t = text.toLowerCase();
    const q = query.toLowerCase();
    if (t.includes(q)) {
      // exact substring match - high score scaled by length
      return 100 + Math.min(50, q.length);
    }

    // subsequence match: count how many characters of q appear in t in order
    let qi = 0;
    for (let i = 0; i < t.length && qi < q.length; i++) {
      if (t[i] === q[qi]) qi++;
    }
    if (qi === 0) return 0;
    const subseqRatio = qi / q.length; // 0..1
    // length penalty: prefer shorter texts when same ratio
    const lengthScore = Math.max(0, 1 - (t.length / 200));
    return Math.round(subseqRatio * 60 + lengthScore * 10);
  };

  // When searching, produce scored & sorted results
  const scoredResults = useMemo(() => {
    const q = searchQuery.trim();
    if (q.length === 0) return [] as FAQItem[];
    const scored: { faq: FAQItem; score: number }[] = filteredFAQs.map((faq) => {
      // score against question and answer and id
      const q1 = fuzzyScore(faq.question, q);
      const q2 = fuzzyScore(faq.answer, q);
      const q3 = fuzzyScore(faq.id, q);
      const q4 = fuzzyScore(faq.category, q);
      const score = Math.max(q1 + 20, q2, q3 + 10, q4 + 5); // boost question matches
      return { faq, score };
    }).filter(s => s.score > 0);
    scored.sort((a, b) => b.score - a.score);
    return scored.map(s => s.faq);
  }, [filteredFAQs, searchQuery]);

  // When searching, group results by category for a nested view
  const groupedResults = useMemo(() => {
    const map: Record<string, FAQItem[]> = {};
    if (searchQuery.trim().length === 0) return map;
    // use scoredResults order to preserve ranking inside groups
    scoredResults.forEach((f) => {
      if (!map[f.category]) map[f.category] = [];
      map[f.category].push(f);
    });
    return map;
  }, [scoredResults, searchQuery]);

  const toggleFAQ = (faqId: string) => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-text-main mb-4">
            Help Center
          </h1>
          <p className="text-xl text-text-muted max-w-2xl mx-auto">
            Find answers to common questions and get help with using NotesCrafter
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="relative">
            <input
              type="text"
              id="help-search"
              name="help-search"
              autoComplete="search"
              aria-label="Search help"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for help..."
              className="w-full px-4 py-3 pl-10 pr-10 text-text-main bg-glass/20 border border-border-glow rounded-lg backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder:text-text-muted/60"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-3.5 h-5 w-5 text-text-muted" />
            {searchQuery.length > 0 && (
              <button
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                className="absolute right-3 top-3.5 h-5 w-5 text-text-muted hover:text-text-main"
                title="Clear search"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeCategory === category.id
                      ? 'bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white'
                      : 'bg-glass/30 backdrop-blur-sm border border-border-glow text-text-main hover:bg-glass/40 hover:border-primary/50'
                  }`}
                >
                  <IconComponent className="w-4 h-4 mr-2" />
                  {category.name}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          <AnimatePresence mode="sync">
            {filteredFAQs.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-lg p-6 text-text-muted"
              >
                No results found for "{searchQuery}". Try different keywords or browse the categories.
              </motion.div>
            ) : (
              // If there's an active search, show grouped (nested) results by category
              searchQuery.trim().length > 0 ? (
                Object.keys(groupedResults).map((catId) => {
                  const items = groupedResults[catId];
                  const cat = categories.find(c => c.id === catId);
                  return (
                    <div key={catId} className="space-y-3">
                      <div className="text-sm font-semibold text-text-muted px-2">{cat ? cat.name : catId}</div>
                      {items.map((faq) => (
                        <motion.div
                          key={faq.id}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-lg overflow-hidden hover:border-primary/50 transition-colors"
                        >
                          <button
                            onClick={() => toggleFAQ(faq.id)}
                            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-glass/40 transition-colors"
                          >
                            <h3 className="text-lg font-medium text-text-main">
                              {faq.question}
                            </h3>
                            <motion.div
                              animate={{ rotate: expandedFAQ === faq.id ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDownIcon className="w-5 h-5 text-text-muted" />
                            </motion.div>
                          </button>

                          <AnimatePresence>
                            {expandedFAQ === faq.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden"
                              >
                                <div className="px-6 pb-4 text-text-muted">
                                  {faq.answer}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      ))}
                    </div>
                  );
                })
              ) : (
                // No search active: show category-scoped list (existing behavior)
                filteredFAQs.map((faq) => (
                  <motion.div
                    key={faq.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                      className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-lg overflow-hidden hover:border-primary/50 transition-colors"
                  >
                    <button
                      onClick={() => toggleFAQ(faq.id)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-glass/40 transition-colors"
                    >
                      <h3 className="text-lg font-medium text-text-main">
                        {faq.question}
                      </h3>
                      <motion.div
                        animate={{ rotate: expandedFAQ === faq.id ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDownIcon className="w-5 h-5 text-text-muted" />
                      </motion.div>
                    </button>

                    <AnimatePresence>
                      {expandedFAQ === faq.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-4 text-text-muted">
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))
              )
            )}
          </AnimatePresence>
        </motion.div>

        {/* Contact Support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="bg-glass/30 backdrop-blur-sm border border-border-glow rounded-lg p-8">
            <h2 className="text-2xl font-bold text-text-main mb-4">
              Still need help?
            </h2>
            <p className="text-text-muted mb-6">
              Cannot find the answer you are looking for? Reach out to our support team.
            </p>
            <motion.button
              onClick={() => {
                const ev = new CustomEvent('navigateToPage', { detail: { page: 'contact-us' } });
                window.dispatchEvent(ev);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-primary/20 hover:shadow-xl transition-all duration-300"
            >
              Contact Support
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HelpCenter;